import { useEffect, useState } from 'react';
import moment from 'moment';

import RcSwitch from 'rc-switch';
import RcSlider from 'rc-slider';
import classNames from 'classnames';
import { Button } from 'react-bootstrap';
import playMenuStyleNames from './styles.less';

import { withPlay } from '../with-play';
import { getAdjacentBin, findBinIndex } from './chart.tsx';

const tsToMoment = timestamp => moment.utc(moment.unix(timestamp / 1000));

function breakdownTimestamp(timestamp) {
    const date = tsToMoment(timestamp);

    return {
        year: date.year(),
        month: date.month() + 1, // Months are zero-indexed
        day: date.date(),
        hour: date.hours(),
        minute: date.minutes(),
        second: date.seconds(),
        millisecond: date.milliseconds(),
    };
}

function compareObjects(obj1, obj2, refObj) {
    return Object.keys(refObj).every(k =>
        refObj[k]
            ? obj1[k] === obj2[k]
            : obj1[k] !== obj2[k]
    );
}

const IntervalTimestamp = ({ styleNames = playMenuStyleNames, binValues, timebar, disabled }) => {
    if(!binValues || disabled) {
        return null;
    }

    const playheadStartIndex = findBinIndex(timebar?.playheadStart + 1, binValues) * 2;
    const startTimestamp = binValues[playheadStartIndex];
    const endTimestamp = binValues[playheadStartIndex + 1]; // assume intervalEnd is same bin

    if(!startTimestamp || !endTimestamp) {
        return null;
    }

    const startTime = breakdownTimestamp(startTimestamp);
    const endTime = breakdownTimestamp(endTimestamp);

    // set default time formatting
    let startTimeFormat = 'DD MMM YYYY, hh:mm:ss A z';
    let endTimeFormat = startTimeFormat;
    let timespanStr = '';
    // optimize time format if a special case is applicable
    if(compareObjects(startTime, endTime, { year: false, month: true, day: true, hour: true, minute: true, second: true, millisecond: true })) {
        startTimeFormat = 'YYYY'
        endTimeFormat = startTimeFormat;
        const differenceInYears = endTime.year - startTime.year;
        timespanStr = ` (${differenceInYears} year${differenceInYears > 1 ? 's' : ''})`;
    } else if(compareObjects(startTime, endTime, { month: false, day: true, hour: true, minute: true, second: true, millisecond: true })) {
        startTimeFormat = 'MMM YYYY';
        endTimeFormat = startTimeFormat;
        const differenceInMonths = (endTime.year - startTime.year) * 12 + (endTime.month - startTime.month);
        timespanStr = ` (${differenceInMonths} month${differenceInMonths > 1 ? 's' : ''})`
    } else if(compareObjects(startTime, endTime, { day: false, hour: true, minute: true, second: true, millisecond: true })) {
        startTimeFormat = 'DD MMM, YYYY';
        endTimeFormat = startTimeFormat;
        const differenceInDays = Math.ceil((endTimestamp - startTimestamp) / (1000 * 60 * 60 * 24));
        if(differenceInDays % 7 === 0) {
            timespanStr = ` (${differenceInDays / 7} week${differenceInDays / 7 > 1 ? 's' : ''})`
        } else {
            timespanStr = ` (${differenceInDays} day${differenceInDays > 1 ? 's' : ''})`
        }
    } else if(compareObjects(startTime, endTime, { hour: false, minute: true, second: true, millisecond: true })) {
        startTimeFormat = 'DD MMM YYYY, hh:mm A z';
        endTimeFormat = startTimeFormat;
        const differenceInHours = Math.ceil((endTimestamp - startTimestamp) / (1000 * 60 * 60));
        timespanStr = ` (${differenceInHours} hour${differenceInHours > 1 ? 's' : ''})`;
    } else if(compareObjects(startTime, endTime, { second: true, millisecond: true })) {
        startTimeFormat = 'DD MMM YYYY, hh:mm A z';
        endTimeFormat = startTimeFormat;
        const differenceInMinutes = Math.ceil((endTimestamp - startTimestamp) / (1000 * 60));
        timespanStr = ` (${differenceInMinutes} minute${differenceInMinutes > 1 ? 's' : ''})`;
    } else if(compareObjects(startTime, endTime, { second: false, millisecond: true })) {
        startTimeFormat = 'DD MMM YYYY, hh:mm:ss A z';
        endTimeFormat = startTimeFormat;
        const differenceInSeconds = Math.ceil((endTimestamp - startTimestamp) / (1000));
        timespanStr = ` (${differenceInSeconds} second${differenceInSeconds > 1 ? 's' : ''})`;
    } else if(compareObjects(startTime, endTime, { millisecond: false })) {
        startTimeFormat = 'DD MMM YYYY, hh:mm:ss.SSS A z';
        endTimeFormat = startTimeFormat;
        const differenceInMilliseconds = Math.ceil(endTimestamp - startTimestamp);
        timespanStr = ` (${differenceInMilliseconds} millisecond${differenceInMilliseconds > 1 ? 's' : ''})`;
    }

    return (
        <div className={styleNames['playmenu-graph-timestamp']}>
            <div className={styleNames['start']}>
                {tsToMoment(startTimestamp).format(startTimeFormat)}
            </div>
            <div className={styleNames['divider']}>-</div>
            <div className={styleNames['end']}>
                {tsToMoment(endTimestamp).format(endTimeFormat) + timespanStr}
            </div>
        </div>
    );
}

export const PlayMenu = withPlay(({
    timebar,
    details,
    onUpdateTimebarMode,
    onUpdateTimebarEnableTimestamp,
    onUpdateTimebarSpeed,
    onUpdateTimebarPlay,
    onUpdateTimebarLoop,
    onUpdateTimebarPlayheadStart,
    styleNames = playMenuStyleNames,
}) => {
    const isPlayMode = timebar?.mode;
    const isTimestampEnabled = isPlayMode && timebar?.enableTimestamp;
    const isPlaying = timebar?.play;
    const isLooping = timebar?.loop;
    const binValues = details?.filtered?.binValues;

    // map speed to slider using a clamped logarithmic function
    // use inverse function for slider to speed
    const speedToSliderVal = (s) => Math.min(Math.max(100 * (Math.log10(s) + 1), 0), 200);
    const sliderValToSpeed = (v) => Math.pow(10, v / 100 - 1).toFixed(2);

    const onToggleMode = () => {
        // set defaults on first enable
        if(isPlayMode === undefined) {
            onUpdateTimebarEnableTimestamp({ value: true });
            onUpdateTimebarLoop({ value: true });
        }

        onUpdateTimebarMode({ value: !isPlayMode ?? true });
        onUpdateTimebarPlayheadStart({value: timebar?.playheadStart ?? binValues?.[0]});
    };
    const onToggleEnableTimestamp = () => {
        onUpdateTimebarEnableTimestamp({ value: !timebar?.enableTimestamp ?? true });
    }
    const onChangeSpeed = (e) => {
        const newSpeed = e.target.value;
        onUpdateTimebarSpeed({value: Number(newSpeed) < 0 ? timebar?.speed : newSpeed});
    };
    const onChangeSpeedSlider = (v) => {
        onChangeSpeed({target: {value: sliderValToSpeed(v)}});
    }
    const onStop = () => {
        onUpdateTimebarPlayheadStart({ value: binValues[0] });
        onUpdateTimebarPlay({ value: false });
    };
    const onStepBackward = () => {
        onUpdateTimebarPlayheadStart({ value: getAdjacentBin({ timebar, details }, true) });
    };
    const onStepForward = () => {
        onUpdateTimebarPlayheadStart({ value: getAdjacentBin({ timebar, details }) })
    };
    const onToggleLoop = () => onUpdateTimebarLoop({ value: !isLooping });
    const onTogglePlay = () => onUpdateTimebarPlay({ value: !isPlaying });

    const speed = timebar?.speed ?? 1;
    const speedContainerWidth = String(speed)
        .split(/[^0-9]/)
        .reduce((width, numStr) => width + (numStr.length * 0.45), 2); // in rem

    // use state to decouple and isolate Slider component from speed prop
    const [sliderVal, setSliderVal] = useState(speedToSliderVal(speed));
    useEffect(() => !Number.isNaN(Number.parseFloat(speed)) && setSliderVal(speedToSliderVal(speed)), [speed]);

    const isFirstBin = timebar?.playheadStart === binValues?.[0];
    return <div className={styleNames['playmenu']}>
        <strong>Play Mode</strong>
        <RcSwitch
            className={styleNames['playmenu-toggle-switch']}
            defaultChecked={isPlayMode}
            checkedChildren={'On'}
            unCheckedChildren={'Off'}
            key={`play-mode-checked`}
            onChange={onToggleMode}
        />
        <div className={classNames(styleNames['playmenu-media-bar'], !isPlayMode && styleNames['hidden'])}>
            <Button
                variant="outline-secondary"
                onClick={onToggleEnableTimestamp}
                className="fa fa-clock-o"
                title="Toggle Timestamp"
                active={isTimestampEnabled}
                disabled={!isPlayMode}
            />
            <Button
                variant="outline-secondary"
                onClick={onTogglePlay}
                className={classNames('fa', isPlaying ? 'fa-pause' : 'fa-play')}
                title={isPlaying ? 'Pause' : (isFirstBin ? 'Play' : 'Resume')}
                disabled={!isPlayMode}
            />
            <Button
                variant="outline-secondary"
                onClick={onStop}
                className="fa fa-stop"
                title="Reset"
                disabled={!isPlayMode || !isPlaying && isFirstBin}
            />
            <Button
                variant="outline-secondary"
                onClick={onStepBackward}
                className="fa fa-backward"
                title="Step Backward"
                disabled={!isPlayMode}
            />
            <Button
                variant="outline-secondary"
                onClick={onStepForward}
                className="fa fa-forward"
                title="Step Forward"
                disabled={!isPlayMode}
            />
            <Button
                variant="outline-secondary"
                onClick={onToggleLoop}
                className="fa fa-refresh"
                title="Toggle Loop"
                active={isLooping}
                disabled={!isPlayMode}
            />
            <div className={styleNames['playmenu-speed-container']}>
                <span><strong>Speed:</strong></span>
                <RcSlider
                    className={styleNames['playmenu-speed-slider']}
                    trackStyle={{backgroundColor: 'transparent'}}
                    handleStyle={{backgroundColor: '#CCC', border: 'solid 2px #BBB'}}
                    onChange={onChangeSpeedSlider}
                    min={0} max={200}
                    startPoint={100}
                    value={sliderVal}
                />
                <div className={styleNames['input-container']} style={{width: `${speedContainerWidth}rem`}}>
                    <input type="number" value={speed} step={0.1} onChange={onChangeSpeed}/>
                </div>
            </div>
        </div>
        <IntervalTimestamp binValues={binValues} timebar={timebar} disabled={!isTimestampEnabled} />
    </div>;
});